// Shared HTTP client factory. Wraps axios with the project's auth-header
// convention and the canonical error envelope shape (see
// plan/reference/conventions.md).
//
// PR 11 ST 2 (factory refactor): client.ts is now pure — it exports
// createApiClient({ baseURL, getToken }) and a configure/get pair around a
// module-level singleton. Each build (CRA today, staff Vite in ST 3, visitor
// Vite in PR 12) calls configureApiClient() once at bootstrap with its own
// env reader (process.env for CRA, import.meta.env for Vite) and token
// getter. shared/ stays build-agnostic.
//
// See memory/project_pr10_api_client_deferred_factory.md.

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { isSessionExpiredCode } from "../lib/staffSession";

// Canonical error envelope returned by the backend. Mirrors
// app/core/exceptions.py register_error_handlers().
export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    correlationId?: string;
  };
}

// Thrown by the client interceptor for any non-2xx response. Carries the
// envelope plus HTTP status + the original axios error for advanced cases.
export class ApiError extends Error {
  readonly code: string;
  readonly status: number | undefined;
  readonly correlationId: string | undefined;
  readonly details: Record<string, unknown> | undefined;
  readonly cause: AxiosError | undefined;

  constructor(
    envelope: ApiErrorEnvelope,
    status: number | undefined,
    cause?: AxiosError,
  ) {
    super(envelope.error.message);
    this.name = "ApiError";
    this.code = envelope.error.code;
    this.status = status;
    this.correlationId = envelope.error.correlationId;
    this.details = envelope.error.details;
    this.cause = cause;
  }
}

const API_PREFIX = "/api";

export interface ApiClientOptions {
  baseURL: string;
  getToken: () => string | null;
}

export function createApiClient({
  baseURL,
  getToken,
}: ApiClientOptions): AxiosInstance {
  const normalized = (baseURL ?? "").replace(/\/$/, "");
  const instance = axios.create({
    baseURL: `${normalized}${API_PREFIX}`,
    headers: { "Content-Type": "application/json" },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<ApiErrorEnvelope>) => {
      const envelope = error.response?.data;
      if (
        error.response?.status === 401 &&
        isSessionExpiredCode(envelope?.error?.code)
      ) {
        _onSessionExpired?.();
      }
      if (envelope?.error?.code) {
        return Promise.reject(
          new ApiError(envelope, error.response?.status, error),
        );
      }
      // Non-envelope failure (network, CORS, timeout, malformed body):
      // synthesize a stable shape so consumers don't have to special-case.
      return Promise.reject(
        new ApiError(
          {
            error: {
              code: "client.network.unavailable",
              message: error.message || "Network request failed",
            },
          },
          error.response?.status,
          error,
        ),
      );
    },
  );

  return instance;
}

let _instance: AxiosInstance | null = null;

// F-W: called when a response says the staff session is gone (expired /
// missing token). The CRA bootstrap wires it to `expireStaffSession`.
let _onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(fn: (() => void) | null): void {
  _onSessionExpired = fn;
}

export function configureApiClient(opts: ApiClientOptions): AxiosInstance {
  _instance = createApiClient(opts);
  return _instance;
}

export function getApiClient(): AxiosInstance {
  if (!_instance) {
    throw new Error(
      "API client not configured. Call configureApiClient() during app bootstrap.",
    );
  }
  return _instance;
}

export type { AxiosRequestConfig };
